import { c as _c } from "react/compiler-runtime";
import { useFormikContext } from 'formik';
import { useContext } from 'react';
import useSWR from 'swr';
import { expandResourcePatternShorthand } from '../../../../../../../../../domain/issue-trigger-patterns';
import { IssueType } from '../../../../../../../../../typings/Issue.interface';
import { APIIssueTrigger, APIIssueTriggerEmailChannel, IssueTriggerBackpressureConfigs, IssueTriggerBetterUptimeChannel, IssueTriggerDeliveryConfigs, IssueTriggerIncidentIoChannel, IssueTriggerIntegrationChannel, IssueTriggerRequestConfigs, IssueTriggerSentryChannel, IssueTriggerSlackChannel, IssueTriggerTransformationConfigs } from '../../../../../../../../../typings/IssueTrigger.interface';
import { RequestRejectionCause } from '../../../../../../../../../typings/Request.interface';
import { TeamIntegration, TeamIntegrationBetterUptimeConfigs, TeamIntegrationIncidentIoConfigs, TeamIntegrationMicrosoftTeamsConfigs, TeamIntegrationProvider, TeamIntegrationSentryPublicConfigs, TeamIntegrationSlackConfigs } from '../../../../../../../../../typings/TeamIntegration.interface';
import { APIWebhook } from '../../../../../../../../../typings/Webhook.interface';
import APIMethodKeys from '../../../../../../client/APIMethodKeys';
import team_integration_schemas from '../../../../../../configs/team-integration-schemas';
import isEmpty from '../../../../../../utils/isEmpty';
import { getMsIntervalByUnit } from '../../../../../../utils/rules';
import Alert from '../../../../../common/base/Alert';
import { StyledCard, StyledCardSection } from '../../../../../common/base/Card';
import Icon from '../../../../../common/base/Icon';
import Link from '../../../../../common/base/Link';
import Text from '../../../../../common/base/Text';
import TextSwitch from '../../../../../common/base/TextSwitch';
import Tooltip from '../../../../../common/base/Tooltip';
import IntervalInput from '../../../../../common/Form/Fields/IntervalInput';
import MultiSelectInput from '../../../../../common/Form/Fields/MultiSelectInput';
import SelectInput from '../../../../../common/Form/Fields/SelectInput';
import SwitchInput from '../../../../../common/Form/Fields/SwitchInput';
import { Div } from '../../../../../common/helpers/StyledUtils';
import { GlobalContext } from '../../../../../contexts/GlobalContext';
import { DashboardContext } from '../../../DashboardContext';
import ResourcePatternInput, { RESOURCE_PATTERN_PREVIEW_LIMIT } from './ResourcePatternInput';
type ReferenceOption = 'select' | 'pattern' | 'all';
interface ChannelValues {
  selected: boolean;
}
interface IntegrationValues extends ChannelValues {}
interface SlackValues extends IntegrationValues {
  channel_name: string;
}
interface BetterUptimeValues extends IntegrationValues {
  escalation_policy_id: string;
  escalation_policy_name: string;
}
interface SentryValues extends IntegrationValues {
  project_id: string;
}
interface EmailValues extends ChannelValues {
  recipient_type: 'all' | 'specific';
  member_emails?: string[];
}
type Channels = Record<Exclude<TeamIntegrationProvider, 'datadog' | 'prometheus'>, SlackValues | BetterUptimeValues | IntegrationValues | EmailValues>;
interface ChannelsValues {
  channels?: Channels;
}
interface DeliveryValues extends IssueTriggerDeliveryConfigs, ChannelsValues {
  reference: ReferenceOption;
  connections: string[];
  pattern: string;
}
interface TransformationValues extends IssueTriggerTransformationConfigs, ChannelsValues {
  reference: ReferenceOption;
  transformations: string[];
  pattern: string;
}
interface BackpressureValues extends IssueTriggerBackpressureConfigs, ChannelsValues {
  reference: ReferenceOption;
  destinations: string[];
  pattern: string;
}
interface RequestValues extends ChannelsValues {
  reference: ReferenceOption;
  sources: string[];
  pattern: string;
  rejection_causes_reference: 'all' | 'select' | 'exclude';
  rejection_causes: RequestRejectionCause[];
  excluded_rejection_causes: RequestRejectionCause[];
}
export const team_channel_forms: Record<string, {
  getInitialValues;
  formatValuesToAPIInput;
  validate;
  Fields;
}> = {
  slack: {
    getInitialValues: (channel: IssueTriggerSlackChannel) => {
      return {
        channel_name: channel.channel_name,
        selected: true
      };
    },
    formatValuesToAPIInput: (values: SlackValues) => {
      return {
        channel_name: values.channel_name
      };
    },
    validate: (values: SlackValues) => {
      if (!values?.channel_name) {
        return {
          channels: {
            slack: {
              channel_name: 'Required'
            }
          }
        };
      }
    },
    Fields: (integration: TeamIntegration) => {
      const {
        channels
      } = integration.configs as TeamIntegrationSlackConfigs;
      return <>
          <SelectInput m={0} block search name={'channels.slack.channel_name'} placeholder={'Select channel...'} required options={channels?.map(channel => ({
          label: channel.name,
          value: channel.name
        })) || []} />
        </>;
    }
  },
  opsgenie: {
    getInitialValues: (channel: IssueTriggerIntegrationChannel) => {
      return {
        selected: true
      };
    },
    formatValuesToAPIInput: (values: IntegrationValues) => {
      return {};
    },
    validate: undefined,
    Fields: undefined
  },
  pagerduty: {
    getInitialValues: (channel: IssueTriggerIntegrationChannel) => {
      return {
        selected: true
      };
    },
    formatValuesToAPIInput: (values: IntegrationValues) => {
      return {};
    },
    validate: undefined,
    Fields: undefined
  },
  email: {
    getInitialValues: (channel: APIIssueTriggerEmailChannel) => {
      return {
        selected: true,
        recipient_type: channel.member_emails == null ? 'all' : 'specific',
        member_emails: channel.member_emails ?? []
      };
    },
    formatValuesToAPIInput: (values: EmailValues): APIIssueTriggerEmailChannel => {
      return {
        member_emails: values.recipient_type === 'all' ? null : values.member_emails ?? []
      };
    },
    validate: (values: EmailValues) => {
      if (values?.recipient_type === 'specific' && isEmpty(values.member_emails)) {
        return {
          channels: {
            email: {
              member_emails: 'Required'
            }
          }
        };
      }
    },
    Fields: undefined
  },
  microsoft_teams: {
    getInitialValues: (channel: IssueTriggerSlackChannel) => {
      return {
        channel_name: channel.channel_name,
        selected: true
      };
    },
    formatValuesToAPIInput: (values: SlackValues) => {
      return {
        channel_name: values.channel_name
      };
    },
    validate: (values: SlackValues) => {
      if (!values?.channel_name) {
        return {
          channels: {
            microsoft_teams: {
              channel_name: 'Required'
            }
          }
        };
      }
    },
    Fields: (integration: TeamIntegration) => {
      const {
        channels
      } = integration.configs as TeamIntegrationMicrosoftTeamsConfigs;
      return <>
          <SelectInput m={0} block search name={'channels.microsoft_teams.channel_name'} placeholder={'Select channel...'} required options={channels?.map(channel => ({
          label: `${channel.team_name} - ${channel.name}`,
          value: `${channel.team_id}:${channel.id}:${channel.name}`
        })) || []} />
        </>;
    }
  },
  discord: {
    getInitialValues: (channel: any) => {
      // Encode channel_id and channel_name to match dropdown option values
      const encoded_value = channel?.channel_id && channel?.channel_name ? `${channel.channel_id}:${channel.channel_name}` : '';
      return {
        channel_name: encoded_value,
        channel_id: channel?.channel_id || '',
        selected: true
      };
    },
    formatValuesToAPIInput: (values: any) => {
      // Parse the encoded value to extract channel_id and channel_name
      // Value format: "channel_id:channel_name"
      const parts = values.channel_name?.split(':') || [];
      const channel_id = parts[0] || '';
      const channel_name = parts[1] || '';
      return {
        channel_name,
        channel_id
      };
    },
    validate: (values: any) => {
      if (!values?.channel_name) {
        return {
          channels: {
            discord: {
              channel_name: 'Required'
            }
          }
        };
      }
    },
    Fields: (integration: TeamIntegration) => {
      const {
        channels
      } = integration.configs as any;
      return <>
          <SelectInput m={0} block search name={'channels.discord.channel_name'} placeholder={'Select channel...'} required options={channels?.map((channel: any) => ({
          label: `#${channel.name}`,
          value: `${channel.id}:${channel.name}` // Encode both id and name in value
        })) || []} />
        </>;
    }
  },
  betteruptime: {
    getInitialValues: (channel: IssueTriggerBetterUptimeChannel) => {
      // Encode escalation_policy_id and escalation_policy_name to match dropdown option values
      const encoded_value = channel?.escalation_policy_id && channel?.escalation_policy_name ? `${channel.escalation_policy_id}:${channel.escalation_policy_name}` : '';
      return {
        escalation_policy_id: encoded_value,
        escalation_policy_name: channel?.escalation_policy_name || '',
        selected: true
      };
    },
    formatValuesToAPIInput: (values: BetterUptimeValues) => {
      // Parse the encoded value to extract escalation_policy_id and escalation_policy_name
      // Value format: "policy_id:policy_name"
      const parts = values.escalation_policy_id?.split(':') || [];
      const escalation_policy_id = parts[0] || '';
      const escalation_policy_name = parts[1] || '';
      return {
        escalation_policy_id,
        escalation_policy_name
      };
    },
    validate: (values: BetterUptimeValues) => {
      if (!values?.escalation_policy_id) {
        return {
          channels: {
            betteruptime: {
              escalation_policy_id: 'Required'
            }
          }
        };
      }
    },
    Fields: (integration: TeamIntegration) => {
      const {
        escalation_policies
      } = integration.configs as TeamIntegrationBetterUptimeConfigs;
      return <>
          <SelectInput m={0} block search name={'channels.betteruptime.escalation_policy_id'} placeholder={'Select escalation policy...'} required label={'Escalation Policy'} options={escalation_policies?.map(policy => ({
          label: policy.name,
          value: `${policy.id}:${policy.name}` // Encode both id and name in value
        })) || []} />
        </>;
    }
  },
  incidentio: {
    getInitialValues: (channel: IssueTriggerIncidentIoChannel) => {
      const encoded_value = channel?.severity_id && channel?.severity_name ? `${channel.severity_id}:${channel.severity_name}` : '';
      return {
        severity_id: encoded_value,
        severity_name: channel?.severity_name || '',
        selected: true
      };
    },
    formatValuesToAPIInput: (values: any) => {
      const parts = values.severity_id?.split(':') || [];
      const severity_id = parts[0] || '';
      const severity_name = parts[1] || '';
      if (!severity_id) {
        return {};
      }
      return {
        severity_id,
        severity_name
      };
    },
    validate: undefined,
    // Severity is optional
    Fields: (integration: TeamIntegration) => {
      const {
        severities
      } = integration.configs as TeamIntegrationIncidentIoConfigs;
      return <>
          <SelectInput m={0} block search name={'channels.incidentio.severity_id'} placeholder={'Select severity (optional)...'} label={'Severity'} options={severities?.map(severity => ({
          label: severity.name,
          value: `${severity.id}:${severity.name}`
        })) || []} />
        </>;
    }
  },
  sentry: {
    getInitialValues: (channel: IssueTriggerSentryChannel) => ({
      project_id: channel?.project_id || '',
      selected: true
    }),
    // The server fills in the project's slug and name from the connected Sentry organization.
    formatValuesToAPIInput: (values: SentryValues) => ({
      project_id: values.project_id
    }),
    validate: (values: SentryValues) => {
      if (!values?.project_id) {
        return {
          channels: {
            sentry: {
              project_id: 'Required'
            }
          }
        };
      }
    },
    Fields: (integration: TeamIntegration) => {
      const {
        projects
      } = integration.configs as TeamIntegrationSentryPublicConfigs;
      return <SelectInput m={0} block search name={'channels.sentry.project_id'} placeholder={'Select project...'} required label={'Sentry Project'} options={projects?.map(project => ({
        label: `${project.name} (${project.slug})`,
        value: String(project.id)
      })) || []} />;
    }
  }
};
const EmailRecipients = () => {
  const $ = _c(13);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    team
  } = useContext(DashboardContext);
  let t0;
  if ($[0] !== team) {
    t0 = team && APIMethodKeys.teams.listMembers(team.id);
    $[0] = team;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== HookdeckAPI.teams) {
    t1 = () => HookdeckAPI.teams.listMembers();
    $[2] = HookdeckAPI.teams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data: members,
    error
  } = useSWR(t0, t1);
  if (error) {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Alert danger={true}>Unable to load project members. Please try again.</Alert>;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  if (!members) {
    let t2;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Text muted={true}>Loading project members...</Text>;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t2;
  if ($[6] !== members) {
    t2 = members.map(_temp).join(",");
    $[6] = members;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== members) {
    t3 = members.map(_temp2);
    $[8] = members;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t2 || $[11] !== t3) {
    t4 = <MultiSelectInput key={t2} m={0} name="channels.email.member_emails" option_title="Project members" search_placeholder="Search members..." initial_options={t3} required={true} help="Project access and notification preferences still apply." />;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
};
const Channels = t0 => {
  const $ = _c(31);
  let {
    integrations,
    unwrapped: t1
  } = t0;
  const unwrapped = t1 === undefined ? false : t1;
  const {
    values,
    setFieldValue
  } = useFormikContext() as any;
  let t2;
  if ($[0] !== values.channels) {
    t2 = values.channels || {};
    $[0] = values.channels;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const channels = t2;
  let t3;
  let t4;
  let t5;
  if ($[2] !== channels || $[3] !== integrations || $[4] !== setFieldValue) {
    let t6;
    if ($[9] !== channels) {
      t6 = integration => {
        if (!integration.features.includes("notification") || integration.expired) {
          delete channels[integration.provider];
        }
        return integration.features.includes("notification") && !integration.expired;
      };
      $[9] = channels;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    integrations = integrations.filter(t6);
    let t7;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        b: 2
      };
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    let t8;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        x: 4,
        y: 3
      };
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    let t9;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <StyledCardSection p={t8}><Div flex={{
          align: "center",
          gap: 3
        }}><SwitchInput name="channels.email.selected" m={0} /><Text>Email</Text></Div></StyledCardSection>;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    let t10;
    if ($[14] !== channels.email || $[15] !== setFieldValue) {
      t10 = channels.email?.selected && <StyledCardSection p={4}><TextSwitch full_width={true} active={channels.email.recipient_type} options={[{
          key: "all",
          label: "All project members"
        }, {
          key: "specific",
          label: "Specific project members"
        }]} onSelect={recipient_type => setFieldValue("channels.email.recipient_type", recipient_type)} />{channels.email.recipient_type === "specific" && <Div m={{
          t: 4
        }}><EmailRecipients /></Div>}</StyledCardSection>;
      $[14] = channels.email;
      $[15] = setFieldValue;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    if ($[17] !== t10) {
      t3 = <StyledCard m={t7}>{t9}{t10}</StyledCard>;
      $[17] = t10;
      $[18] = t3;
    } else {
      t3 = $[18];
    }
    t4 = integrations?.map(integration_0 => <StyledCard m={{
      b: 2
    }} key={integration_0.id}><StyledCardSection p={{
        x: 4,
        y: 3
      }}><Div flex={{
          align: "center",
          gap: 3
        }}><SwitchInput name={`channels.${integration_0.provider}.selected`} m={0} /><Text><Icon icon={team_integration_schemas[integration_0.provider].icon} left={true} />{team_integration_schemas[integration_0.provider].label}</Text></Div></StyledCardSection>{channels[integration_0.provider]?.selected && team_channel_forms[integration_0.provider].Fields && <StyledCardSection p={4}>{team_channel_forms[integration_0.provider].Fields(integration_0)}</StyledCardSection>}</StyledCard>);
    t5 = isEmpty(integrations) && <Div m={{
      t: 4
    }} flex={{
      align: "center",
      gap: 1
    }}><Icon icon="info" muted={true} /><Text muted={true}>To receive notifications in third-party platforms{" "}<Link to="/settings/project/integrations" target="_blank">configure an integration →</Link></Text></Div>;
    $[2] = channels;
    $[3] = integrations;
    $[4] = setFieldValue;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = integrations;
  } else {
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    integrations = $[8];
  }
  let t6;
  if ($[19] !== t3 || $[20] !== t4 || $[21] !== t5) {
    t6 = <>{t3}{t4}{t5}</>;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const channel_fields = t6;
  if (unwrapped) {
    let t7;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Text semi={true} muted={true} size="xs" m={{
        b: 1
      }}>Notification Channels</Text>;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    let t8;
    if ($[24] !== channel_fields) {
      t8 = <>{t7}{channel_fields}</>;
      $[24] = channel_fields;
      $[25] = t8;
    } else {
      t8 = $[25];
    }
    return t8;
  }
  let t7;
  let t8;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      align: "center",
      gap: 1
    };
    t8 = <Text semi={true}>Notification channels</Text>;
    $[26] = t7;
    $[27] = t8;
  } else {
    t7 = $[26];
    t8 = $[27];
  }
  let t9;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <StyledCardSection borderless={true} m={4} flex={t7}>{t8}<Tooltip tooltip="Specify the channels that should be notified when the issue is triggered."><Icon icon="info" muted={true} /></Tooltip></StyledCardSection>;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== channel_fields) {
    t10 = <StyledCard borderless={true}>{t9}<StyledCardSection borderless={true} m={4}>{channel_fields}</StyledCardSection></StyledCard>;
    $[29] = channel_fields;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  return t10;
};
const getChannelsInitialValues = (integrations: TeamIntegration[], issue_trigger?: APIIssueTrigger) => {
  if (issue_trigger && !isEmpty(issue_trigger.channels)) {
    return Object.entries(issue_trigger.channels)?.reduce((prev, [provider, channel]) => {
      return {
        ...prev,
        [provider]: {
          ...team_channel_forms[provider].getInitialValues(channel)
        }
      };
    }, {
      email: {
        selected: false,
        recipient_type: 'all',
        member_emails: []
      }
    } as any);
  }
  if (!issue_trigger) {
    return integrations.reduce((prev, integration) => {
      return {
        ...prev,
        [integration.provider]: {
          selected: true
        }
      };
    }, {
      email: {
        selected: true,
        recipient_type: 'all',
        member_emails: []
      }
    } as any);
  }
  return {
    email: {
      selected: false,
      recipient_type: 'all',
      member_emails: []
    }
  };
};
const formatChannelsValuesToAPIInput = (channels?: Channels) => {
  return Object.entries(channels || {}).filter(([, value]) => value.selected).reduce((prev, [provider, value]) => {
    return {
      ...prev,
      [provider]: team_channel_forms[provider].formatValuesToAPIInput(value)
    };
  }, {} as any);
};
const validateChannels = (errors: any, channels: Channels) => {
  if (isEmpty(channels) || isEmpty(Object.entries(channels).filter(([, channel]) => channel.selected))) {
    return errors;
  } else {
    Object.entries(channels).forEach(([key, channel]) => {
      const {
        validate
      } = team_channel_forms[key];
      if (!validate || !channel.selected) {
        return;
      }
      errors = {
        ...errors,
        ...validate(channel)
      };
    });
  }
  return errors;
};

// Issue triggers match a connection by its own name, not "<source> -> <name>"
const getConnectionName = (webhook: APIWebhook) => webhook.full_name.split(' -> ')[1];
const issue_trigger_forms: Record<IssueType, {
  getInitialValues;
  formatValuesToAPIInput;
  Fields;
  validate?;
}> = {
  delivery: {
    getInitialValues: (integrations: TeamIntegration[], issue_trigger?: APIIssueTrigger): DeliveryValues => {
      const configs = issue_trigger?.configs as IssueTriggerDeliveryConfigs;
      let reference = 'all';
      if (Array.isArray(configs?.connections)) {
        reference = 'select';
      } else if (configs?.connections !== '*' && configs?.connections.length > 0) {
        reference = 'pattern';
      }
      return {
        strategy: configs?.strategy || 'first_attempt',
        reference: reference as ReferenceOption,
        pattern: reference === 'pattern' ? configs?.connections as string : '',
        connections: reference === 'select' ? configs.connections as string[] : [],
        channels: getChannelsInitialValues(integrations, issue_trigger)
      };
    },
    validate: (values: {
      channels: Channels;
      channels_error: string;
    }): {
      channels?: Channels;
      channels_error?: string;
    } => {
      let errors = {} as {
        channels?: Channels;
        channels_error?: string;
      };
      errors = validateChannels(errors, values.channels);
      return errors;
    },
    formatValuesToAPIInput: (values: DeliveryValues) => {
      return {
        type: 'delivery',
        configs: {
          strategy: values.strategy,
          connections: values.reference === 'all' ? '*' : values.reference === 'pattern' ? expandResourcePatternShorthand(values.pattern) : values.connections
        },
        channels: formatChannelsValuesToAPIInput(values.channels)
      };
    },
    Fields: ({
      integrations
    }) => {
      const {
        HookdeckAPI
      } = useContext(GlobalContext);
      const {
        values,
        setFieldValue
      } = useFormikContext<DeliveryValues>();
      const getLabel = model => model.full_name;
      const formatOptions = data => data && data.models.map(model => ({
        label: getLabel(model),
        value: model.id
      }));
      return <>
          <Div m={{
          b: 5
        }}>
            <Text semi muted size="xs" m={{
            b: 1
          }}>
              Impacted Connections
            </Text>
            <TextSwitch full_width active={values.reference} options={[{
            key: 'all',
            label: 'All connections'
          }, {
            key: 'select',
            label: 'Specific connections'
          }, {
            key: 'pattern',
            label: 'Matching pattern'
          }]} onSelect={reference => setFieldValue('reference', reference)} />
            {values.reference === 'select' && <MultiSelectInput m={{
            t: 4
          }} initial_options={[]} option_monospace fetcher={HookdeckAPI.webhooks.list} fetcherKey={APIMethodKeys.webhooks.list} formatOptions={formatOptions} formatFilters={(search_term?: string, values?: string[]) => ({
            limit: 250,
            full_name: search_term ? search_term : undefined,
            id: values
          })} name="connections" required />}
            {values.reference === 'pattern' && <ResourcePatternInput name="pattern" label="connections" swr_key={APIMethodKeys.webhooks.list({
            limit: RESOURCE_PATTERN_PREVIEW_LIMIT
          })} fetcher={() => HookdeckAPI.webhooks.list({
            limit: RESOURCE_PATTERN_PREVIEW_LIMIT
          })} getName={getConnectionName} />}
          </Div>
          <Div m={{
          b: 5
        }}>
            <Text semi muted size="xs" m={{
            b: 1
          }}>
              Open Issue After
            </Text>
            <TextSwitch full_width active={values.strategy} options={[{
            key: 'first_attempt',
            label: 'First delivery attempt'
          }, {
            key: 'final_attempt',
            label: 'Final delivery attempt'
          }]} onSelect={strategy => setFieldValue('strategy', strategy)} />
          </Div>
          <Channels integrations={integrations} unwrapped />
        </>;
    }
  },
  transformation: {
    getInitialValues: (integrations: TeamIntegration[], issue_trigger?: APIIssueTrigger): TransformationValues => {
      const configs = issue_trigger?.configs as IssueTriggerTransformationConfigs;
      let reference = 'all';
      if (Array.isArray(configs?.transformations)) {
        reference = 'select';
      } else if (configs?.transformations !== '*' && configs?.transformations.length > 0) {
        reference = 'pattern';
      }
      return {
        log_level: configs?.log_level || 'warn',
        reference: reference as ReferenceOption,
        pattern: reference === 'pattern' ? configs?.transformations as string : '',
        transformations: reference === 'select' ? configs.transformations as string[] : [],
        channels: getChannelsInitialValues(integrations, issue_trigger)
      };
    },
    validate: (values: {
      channels: Channels;
      channels_error: string;
    }): {
      channels?: Channels;
      channels_error?: string;
    } => {
      let errors = {} as {
        channels?: Channels;
        channels_error?: string;
      };
      errors = validateChannels(errors, values.channels);
      return errors;
    },
    formatValuesToAPIInput: (values: TransformationValues) => {
      return {
        type: 'transformation',
        configs: {
          log_level: values.log_level || 'warn',
          transformations: values.reference === 'all' ? '*' : values.reference === 'pattern' ? expandResourcePatternShorthand(values.pattern) : values.transformations
        },
        channels: formatChannelsValuesToAPIInput(values.channels)
      };
    },
    Fields: ({
      integrations
    }) => {
      const {
        HookdeckAPI
      } = useContext(GlobalContext);
      const {
        values,
        setFieldValue
      } = useFormikContext<TransformationValues>();
      const formatOptions = data => data && data.models.map(model => ({
        label: model.name,
        value: model.id
      }));
      return <>
          <Div m={{
          b: 5
        }}>
            <Text semi muted size="xs" m={{
            b: 1
          }}>
              Impacted Transformations
            </Text>
            <TextSwitch full_width active={values.reference} options={[{
            key: 'all',
            label: 'All transformations'
          }, {
            key: 'select',
            label: 'Specific transformations'
          }, {
            key: 'pattern',
            label: 'Matching pattern'
          }]} onSelect={reference => setFieldValue('reference', reference)} />
            {values.reference === 'select' && <MultiSelectInput m={{
            t: 4
          }} initial_options={[]} fetcher={HookdeckAPI.transformations.list} fetcherKey={APIMethodKeys.transformations.list} formatFilters={(search_term?: string, values?: string[]) => ({
            limit: 250,
            name: search_term ? {
              contains: search_term
            } : undefined,
            id: values
          })} formatOptions={formatOptions} name="transformations" required />}
            {values.reference === 'pattern' && <ResourcePatternInput name="pattern" label="transformations" swr_key={APIMethodKeys.transformations.list({
            limit: RESOURCE_PATTERN_PREVIEW_LIMIT
          })} fetcher={() => HookdeckAPI.transformations.list({
            limit: RESOURCE_PATTERN_PREVIEW_LIMIT
          })} getName={transformation => transformation.name} />}
          </Div>
          <Div m={{
          b: 5
        }}>
            <Text semi muted size="xs" m={{
            b: 1
          }}>
              Log Level
            </Text>
            <TextSwitch full_width active={values.log_level} options={[{
            key: 'warn',
            label: 'Warn'
          }, {
            key: 'error',
            label: 'Error'
          }, {
            key: 'fatal',
            label: 'Fatal'
          }]} onSelect={log_level => setFieldValue('log_level', log_level)} />
          </Div>
          <Channels integrations={integrations} unwrapped />
        </>;
    }
  },
  backpressure: {
    getInitialValues: (integrations: TeamIntegration[], issue_trigger?: APIIssueTrigger): BackpressureValues & {
      time_unit: {
        delay: string;
      };
    } => {
      const configs = issue_trigger?.configs as IssueTriggerBackpressureConfigs;
      let reference = 'all';
      if (Array.isArray(configs?.destinations)) {
        reference = 'select';
      } else if (configs?.destinations !== '*' && configs?.destinations.length > 0) {
        reference = 'pattern';
      }
      const [, time_unit] = getMsIntervalByUnit(configs?.delay || 1000 * 60 * 10, 'delay');
      return {
        delay: configs?.delay || 1000 * 60 * 10,
        reference: reference as ReferenceOption,
        pattern: reference === 'pattern' ? configs?.destinations as string : '',
        destinations: reference === 'select' ? configs.destinations as string[] : [],
        channels: getChannelsInitialValues(integrations, issue_trigger),
        time_unit: {
          delay: time_unit
        }
      };
    },
    validate: (values: {
      delay: number;
      channels: Channels;
      channels_error: string;
    }): {
      delay?: string;
      channels?: Channels;
      channels_error?: string;
    } => {
      let errors = {} as {
        delay?: string;
        channels?: Channels;
        channels_error?: string;
      };
      if (values.delay < 1000 * 60) {
        errors.delay = 'Delay must be at least 1 minute';
      } else if (values.delay > 1000 * 60 * 60 * 24) {
        errors.delay = 'Delay must be at most 1 day';
      }
      errors = validateChannels(errors, values.channels);
      return errors;
    },
    formatValuesToAPIInput: (values: BackpressureValues) => {
      return {
        type: 'backpressure',
        configs: {
          delay: values.delay,
          destinations: values.reference === 'all' ? '*' : values.reference === 'pattern' ? expandResourcePatternShorthand(values.pattern) : values.destinations
        },
        channels: formatChannelsValuesToAPIInput(values.channels)
      };
    },
    Fields: ({
      integrations
    }) => {
      const {
        HookdeckAPI
      } = useContext(GlobalContext);
      const {
        values,
        setFieldValue
      } = useFormikContext<BackpressureValues>();
      const formatOptions = data => data && data.models.map(model => ({
        label: model.name,
        value: model.id
      }));
      return <>
          <Div m={{
          b: 5
        }}>
            <Text semi muted size="xs" m={{
            b: 1
          }}>
              Impacted Destinations
            </Text>
            <TextSwitch full_width active={values.reference} options={[{
            key: 'all',
            label: 'All destinations'
          }, {
            key: 'select',
            label: 'Specific destinations'
          }, {
            key: 'pattern',
            label: 'Matching pattern'
          }]} onSelect={reference => setFieldValue('reference', reference)} />
            {values.reference === 'select' && <MultiSelectInput m={{
            t: 4
          }} initial_options={[]} fetcher={HookdeckAPI.destinations.list} fetcherKey={APIMethodKeys.destinations.list} formatFilters={(search_term?: string, values?: string[]) => ({
            limit: 250,
            name: search_term ? {
              contains: search_term
            } : undefined,
            id: values
          })} formatOptions={formatOptions} name="destinations" required />}
            {values.reference === 'pattern' && <ResourcePatternInput name="pattern" label="destinations" swr_key={APIMethodKeys.destinations.list({
            limit: RESOURCE_PATTERN_PREVIEW_LIMIT
          })} fetcher={() => HookdeckAPI.destinations.list({
            limit: RESOURCE_PATTERN_PREVIEW_LIMIT
          })} getName={destination => destination.name} />}
          </Div>
          <Div m={{
          b: 5
        }}>
            <Text semi muted size="xs" m={{
            b: 1
          }}>
              Delay
            </Text>
            <IntervalInput min_interval="minutes" name="delay" z_index={1090} />
          </Div>
          <Channels integrations={integrations} unwrapped />
        </>;
    }
  },
  request: {
    getInitialValues: (integrations: TeamIntegration[], issue_trigger?: APIIssueTrigger): RequestValues => {
      const configs = issue_trigger?.configs as IssueTriggerRequestConfigs;
      let reference: ReferenceOption = 'all';
      if (Array.isArray(configs?.sources)) {
        reference = 'select';
      } else if (configs?.sources && configs?.sources !== '*' && configs?.sources.length > 0) {
        reference = 'pattern';
      }
      let rejection_causes_reference: 'all' | 'select' | 'exclude' = 'all';
      let rejection_causes: RequestRejectionCause[] = [];
      let excluded_rejection_causes: RequestRejectionCause[] = [];
      // Check if exclusions are set (rejection_causes is '*' and excluded_rejection_causes has values)
      if (configs?.excluded_rejection_causes && Array.isArray(configs.excluded_rejection_causes) && configs.excluded_rejection_causes.length > 0) {
        rejection_causes_reference = 'exclude';
        excluded_rejection_causes = configs.excluded_rejection_causes;
      } else if (configs?.rejection_causes !== '*' && Array.isArray(configs?.rejection_causes)) {
        rejection_causes_reference = 'select';
        rejection_causes = configs.rejection_causes;
      }
      return {
        rejection_causes_reference,
        rejection_causes,
        excluded_rejection_causes,
        reference: reference,
        pattern: reference === 'pattern' ? configs?.sources as string : '',
        sources: reference === 'select' ? configs.sources as string[] : [],
        channels: getChannelsInitialValues(integrations, issue_trigger)
      };
    },
    validate: (values: {
      channels: Channels;
      channels_error: string;
    }): {
      channels?: Channels;
      channels_error?: string;
    } => {
      let errors = {} as {
        channels?: Channels;
        channels_error?: string;
      };
      errors = validateChannels(errors, values.channels);
      return errors;
    },
    formatValuesToAPIInput: (values: RequestValues) => {
      // Build configs object based on rejection_causes_reference
      const configs: {
        rejection_causes: RequestRejectionCause[] | '*';
        excluded_rejection_causes?: RequestRejectionCause[];
        sources: string | string[];
      } = {
        rejection_causes: '*',
        sources: values.reference === 'all' ? '*' : values.reference === 'pattern' ? expandResourcePatternShorthand(values.pattern) : values.sources
      };
      if (values.rejection_causes_reference === 'select') {
        // Specific causes selected - use array
        configs.rejection_causes = values.rejection_causes;
      } else if (values.rejection_causes_reference === 'exclude') {
        // Excluded causes - rejection_causes must be '*' and exclusions are set
        configs.rejection_causes = '*';
        configs.excluded_rejection_causes = values.excluded_rejection_causes;
      }
      // 'all' case: rejection_causes stays '*' and no exclusions

      return {
        type: 'request',
        configs,
        channels: formatChannelsValuesToAPIInput(values.channels)
      };
    },
    Fields: ({
      integrations
    }) => {
      const {
        HookdeckAPI
      } = useContext(GlobalContext);
      const {
        values,
        setFieldValue
      } = useFormikContext<RequestValues>();
      const formatOptions = data => data && data.models.map(model => ({
        label: model.name,
        value: model.id
      }));
      const rejection_cause_options = [{
        value: 'SOURCE_DISABLED',
        label: 'Source Disabled'
      }, {
        value: 'NO_CONNECTION',
        label: 'No Connection'
      }, {
        value: 'VERIFICATION_FAILED',
        label: 'Verification Failed'
      }, {
        value: 'UNSUPPORTED_HTTP_METHOD',
        label: 'Unsupported HTTP Method'
      }, {
        value: 'UNSUPPORTED_CONTENT_TYPE',
        label: 'Unsupported Content Type'
      }, {
        value: 'UNPARSABLE_JSON',
        label: 'Unparsable JSON'
      }, {
        value: 'PAYLOAD_TOO_LARGE',
        label: 'Payload Too Large'
      }, {
        value: 'INGESTION_FATAL',
        label: 'Ingestion Error'
      }, {
        value: 'UNKNOWN',
        label: 'Unknown'
      }];
      return <>
          <Div m={{
          b: 5
        }}>
            <Text semi muted size="xs" m={{
            b: 1
          }}>
              Impacted Sources
            </Text>
            <TextSwitch full_width active={values.reference} options={[{
            key: 'all',
            label: 'All sources'
          }, {
            key: 'select',
            label: 'Specific sources'
          }, {
            key: 'pattern',
            label: 'Matching pattern'
          }]} onSelect={reference => setFieldValue('reference', reference)} />
            {values.reference === 'select' && <MultiSelectInput m={{
            t: 4
          }} initial_options={[]} fetcher={HookdeckAPI.sources.list} fetcherKey={APIMethodKeys.sources.list} formatFilters={(search_term?: string, values?: string[]) => ({
            limit: 250,
            name: search_term ? {
              contains: search_term
            } : undefined,
            id: values
          })} formatOptions={formatOptions} name="sources" required />}
            {values.reference === 'pattern' && <ResourcePatternInput name="pattern" label="sources" swr_key={APIMethodKeys.sources.list({
            limit: RESOURCE_PATTERN_PREVIEW_LIMIT
          })} fetcher={() => HookdeckAPI.sources.list({
            limit: RESOURCE_PATTERN_PREVIEW_LIMIT
          })} getName={source => source.name} />}
          </Div>
          <Div m={{
          b: 5
        }}>
            <Text semi muted size="xs" m={{
            b: 1
          }}>
              Rejection Causes
            </Text>
            <TextSwitch full_width active={values.rejection_causes_reference} options={[{
            key: 'all',
            label: 'All causes'
          }, {
            key: 'select',
            label: 'Specific causes'
          }, {
            key: 'exclude',
            label: 'Excluded causes'
          }]} onSelect={reference => setFieldValue('rejection_causes_reference', reference)} />
            {values.rejection_causes_reference === 'select' && <MultiSelectInput m={{
            t: 4
          }} name="rejection_causes" initial_options={rejection_cause_options} required />}
            {values.rejection_causes_reference === 'exclude' && <MultiSelectInput m={{
            t: 4
          }} name="excluded_rejection_causes" initial_options={rejection_cause_options} required help="Issues will be triggered for all causes except those selected above." />}
          </Div>
          <Channels integrations={integrations} unwrapped />
        </>;
    }
  }
};
export default issue_trigger_forms;
function _temp(member) {
  return `${member.id}:${member.user_email}`;
}
function _temp2(member_0) {
  return {
    value: member_0.user_email,
    label: member_0.user_name ? `${member_0.user_name} (${member_0.user_email})` : member_0.user_email
  };
}